import React, {useMemo, useState} from 'react';

import {type OverlayProps, PopoverForm} from 'app/general/overlays';
import {useTeams} from 'app/teams/api';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';
import {gt} from 'app/utils/i18n';

import {GenericPopover} from './GenericPopover';

import type {TeamSerializedType} from 'sweet-types';

interface Props {
  overlayProps: OverlayProps;
  attached?: Array<TeamSerializedType>;
  itemURL: string;
  addItemToTeam: (team: TeamSerializedType, url: string) => Promise<unknown>;
  removeItemsFromTeam: (team: TeamSerializedType) => Promise<unknown>;
  onSuccess: () => Promise<unknown>;
  getTeamFilterParams: () =>
    | {exclude_document_url: string}
    | {exclude_user_id: string}
    | null;
}

export function AddToTeamPopover({
  overlayProps,
  itemURL,
  attached,
  addItemToTeam,
  onSuccess,
  getTeamFilterParams,
  removeItemsFromTeam,
}: Props) {
  const [search, setSearch] = useState<string>('');

  const {data: teams} = useTeams(
    convertLocationParamsToFetchParams({
      search,
      ...getTeamFilterParams(),
    }),
    {refetchOnMountOrArgChange: false}
  );

  const unAttachedTeams = useMemo(() => {
    return teams?.results.filter(
      (team) => !attached?.some((attachedTeam) => team.id === attachedTeam.id)
    );
  }, [teams, attached]);

  function doAddItemToTeam(team) {
    return addItemToTeam(team, itemURL).then(onSuccess);
  }

  function onRemove(team) {
    return removeItemsFromTeam(team).then(onSuccess);
  }
  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close}) => {
        return (
          <GenericPopover<TeamSerializedType>
            topHeading={gt('Teams')}
            bottomHeading={gt('Add to team')}
            noResultsHeading={gt('No teams found')}
            noResultsIcon="fa-users"
            loadingText={gt('Loading teams')}
            addTooltipText={gt('Add to this team')}
            removeTooltipText={gt('Remove from this team')}
            attachedItems={attached}
            unAttachedItems={unAttachedTeams}
            search={search}
            onSearch={setSearch}
            onAdd={doAddItemToTeam}
            onRemove={onRemove}
            onClose={close}
          />
        );
      }}
    </PopoverForm>
  );
}
